/**
 * Monitors network conditions and provides adaptive buffer recommendations
 * @class
 */
export class NetworkMonitor {
  constructor() {
    this.callbacks = [];
    this.currentSpeed = 'medium';
    this.lastMeasurement = Date.now();
    this.measurementInterval = 30000; // 30 seconds
    
    this.startMonitoring();
  }

  /**
   * Start monitoring network conditions
   */
  startMonitoring() {
    // Use Network Information API if available
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection) {
        connection.addEventListener('change', () => {
          this.detectSpeed();
        });
        
        this.detectSpeed();
      }
    } else {
      // Fallback: assume medium speed initially
      this.currentSpeed = 'medium';
    }
  }

  /**
   * Detect network speed using Network Information API
   */
  detectSpeed() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (!connection) {
      return;
    }

    const effectiveType = connection.effectiveType;
    let speed;

    // Map effective connection type to speed category
    if (effectiveType === '4g' || effectiveType === 'wifi') {
      speed = 'fast';
    } else if (effectiveType === '3g') {
      speed = 'medium';
    } else if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      speed = 'slow';
    } else {
      speed = 'medium'; // default
    }

    if (speed !== this.currentSpeed) {
      this.currentSpeed = speed;
      this.notifyCallbacks(speed);
    }
  }

  /**
   * Get recommended buffer size in milliseconds based on network speed
   * @returns {number} Buffer size in milliseconds
   */
  getRecommendedBufferMs() {
    // Keep the startup jitter buffer small. A large buffer (we previously used
    // 2000ms on fast networks) adds that much latency before each item starts
    // playing and makes the per-item start threshold large, which made the
    // lead-in of answers feel lost/late. Backend delivers chunks well faster
    // than realtime, so a few hundred ms is plenty; the adaptive bump on
    // underrun (see buffer.underrun handler) raises it if a slow link needs it.
    switch (this.currentSpeed) {
      case 'fast':
        return 300; // WiFi/4G
      case 'medium':
        return 600; // 3G
      case 'slow':
        return 1000; // Slow 3G/2G
      default:
        return 500;
    }
  }

  /**
   * Get recommended minimum buffer chunks before starting playback
   * @returns {number} Number of chunks to buffer
   */
  getRecommendedMinChunks() {
    switch (this.currentSpeed) {
      case 'fast':
        return 2; // Start quickly
      case 'medium':
        return 4; // Balanced
      case 'slow':
        return 6; // Wait for more buffering
      default:
        return 3; // default
    }
  }

  /**
   * Register callback for speed changes
   * @param {Function} callback - Called with (speed: 'fast' | 'medium' | 'slow')
   */
  onSpeedChange(callback) {
    this.callbacks.push(callback);
    // Immediately call with current speed
    callback(this.currentSpeed);
  }

  /**
   * Notify all registered callbacks
   * @param {string} speed - Current network speed
   */
  notifyCallbacks(speed) {
    console.log(`[NetworkMonitor] Network speed changed to: ${speed}`);
    this.callbacks.forEach(cb => {
      try {
        cb(speed);
      } catch (e) {
        console.error('[NetworkMonitor] Callback error:', e);
      }
    });
  }

  /**
   * Get current network speed
   * @returns {string} 'fast' | 'medium' | 'slow'
   */
  getCurrentSpeed() {
    return this.currentSpeed;
  }

  /**
   * Get network information for debugging
   * @returns {object} Network info
   */
  getNetworkInfo() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (!connection) {
      return {
        speed: this.currentSpeed,
        effectiveType: 'unknown',
        downlink: 'unknown',
        rtt: 'unknown',
        supported: false
      };
    }

    return {
      speed: this.currentSpeed,
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink ? `${connection.downlink} Mbps` : 'unknown',
      rtt: connection.rtt ? `${connection.rtt} ms` : 'unknown',
      saveData: connection.saveData || false,
      supported: true
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    this.callbacks = [];
    
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.removeEventListener('change', this.detectSpeed);
    }
  }
}
